import "./App.css";
import { useEffect } from "react";

import React from "react";

import {
  createBrowserRouter,
  RouterProvider,
  redirect,
  useNavigate,
} from "react-router-dom";
import "./index.css";
import Root from "./routes/Root";
import Login from "./routes/Login";
import Index from "./routes/Index";
import Users, { loader as usersLoader } from "./routes/admin/Users";
import User, { loader as userLoader, userAction } from "./routes/admin/User";
import ErrorPage from "./ErrorPage";
import Logout from "./routes/Logout";
import { entityAction } from "./util/actions";
import {
  bannerSchema,
  territorySchema,
  manufacturerSchema,
  productSchema,
  promotionSchema,
  representativeSchema,
  storeSchema,
} from "./util/schemas";
import { getEntity, getEntities, getManufacturerPresets, getManufacturerPreset, SERVER_API } from "./API";
import Banners from "./routes/admin/Banners";
import Territories from "./routes/admin/Territories";
import Territory from "./routes/admin/Territory";
import Banner from "./routes/admin/Banner";
import Stores from "./routes/admin/Stores";
import Store from "./routes/admin/Store";
import Manufacturers from "./routes/admin/Manufacturers";
import Manufacturer from "./routes/admin/Manufacturer";
import Representatives, { loader as representativesLoader } from "./routes/admin/Representatives";
import Representative from "./routes/admin/Representative";
import DashboardConfigs from "./routes/admin/DashboardConfigs";
import ManufacturerPresets from "./routes/admin/ManufacturerPresets";
import ManufacturerPreset from "./routes/admin/ManufacturerPreset";
import Products from "./routes/admin/Products";
import Product from "./routes/admin/Product";
import TopRoute from "./routes/TopRoute";
import Tasks from "./routes/features/Tasks";
import Task from "./routes/features/Task";
import HotSheets, { loader as hotSheetsLoader } from "./routes/features/HotSheets";
import HotSheet, { loader as hotSheetLoader } from "./routes/features/HotSheet";
import Bookings, { loader as bookingsLoader } from "./routes/features/Bookings";
import Booking, { loader as bookingLoader } from "./routes/features/Booking";
import Promotion from "./routes/features/Promotion";
import Promotions from "./routes/features/Promotions";
import PhotoHashtag from "./routes/features/PhotoHashtag";
import PhotoHashtags from "./routes/features/PhotoHashtags";
import Report from "./routes/reports/Report";
import ReportsLayout from "./routes/reports/ReportsLayout";
import DistributionAuditReport from "./routes/reports/DistributionAuditReport";
import DistributionDetailReport from "./routes/reports/DistributionDetailReport";
import DistributionByBannerByItemReport from "./routes/reports/DistributionByBannerByItemReport";
import TotalDistributionByBannerReport from "./routes/reports/TotalDistributionByBannerReport";
import DistributionSummaryReport from "./routes/reports/DistributionSummaryReport";
import Dashboard from "./routes/Dashboard";
import MonthlyMerchandisingReport from "./routes/reports/MonthlyMerchandisingReport";
import BannerByMonthReport from "./routes/reports/BannerByMonthReport";
import PicturesReport from "./routes/reports/PicturesReport";
import TaskReport from "./routes/reports/TaskReport";
import VisitReport from "./routes/performance/VisitReport";
import SharedPicturesReport from "./routes/reports/SharedPicturesReport";
import CallCompletionReport from "./routes/performance/CallCompletionReport";
import DistributionDropReport from "./routes/performance/DistributionDropReport";
import PhotoCaptureReport from "./routes/performance/PhotoCaptureReport";
import CallTimeReport from "./routes/performance/CallTimeReport";
import RepReport from "./routes/performance/RepReport";
import PerformanceLayout from "./routes/performance/PerformanceLayout";
import SharedDashboardReport from "./routes/SharedDashboardReport";
import { topMenu } from "./Constants";

const AUTH_API = `${SERVER_API}/auth`;

const adminItems = topMenu.find((m) => m.href === "admin").sideBarItems;
const featuresItems = topMenu.find((m) => m.href === "features").sideBarItems;
const reportsItems = topMenu.find((m) => m.href === "reports").sideBarItems;
const performanceItems = topMenu.find((m) => m.href === "performance").sideBarItems;

async function authLoader() {
  try {
    const response = await fetch(`${AUTH_API}/me`, { credentials: "include" });
    if (!response.ok) return redirect("/login");
    return response.json();
  } catch {
    return redirect("/login");
  }
}

async function loginAction({ request }) {
  const formData = await request.formData();
  const response = await fetch(`${AUTH_API}/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({
      username: formData.get("username"),
      password: formData.get("password"),
    }),
  });
  if (!response.ok) return { error: "Invalid username or password" };
  const data = await response.json();
  const redirects = {
    REPRESENTATIVE: "/markit/performance/rep_report",
    MANAGEMENT:     "/markit/performance/call_completion",
    REPORTS:        "/markit/reports/distribution_audit",
    SYSTEM:         "/markit/admin",
    MANUFACTURER:   "/markit/reports/distribution_by_banner_by_item",
  };
  return redirect(redirects[data.type] ?? "/markit/admin");
}

async function logoutLoader() {
  await fetch(`${AUTH_API}/logout`, { method: "POST", credentials: "include" });
  return redirect("/login");
}

function App() {
  const router = createBrowserRouter([
    {
      path: "/",
      element: <Index />,
      HydrateFallback: Fallback,
      children: [
        {
          index: true,
          element: <RedirectToLogin />,
        },
        {
          path: "login",
          action: loginAction,
          element: <Login />,
        },
        {
          path: "shared/photos/:token",
          element: <SharedPicturesReport />,
        },
        {
          path: "shared/dashboard/:token",
          element: <SharedDashboardReport />,
        },
        {
          id: "markit",
          path: "markit",
          loader: authLoader,
          element: <Root />,
          children: [
            {
              path: "logout",
              loader: logoutLoader,
              element: <Logout />,
            },
            {
              path: "admin",
              element: <TopRoute sideBarItems={adminItems} />,
              children: [
                {
                  index: true,
                  loader: () => redirect("/markit/admin/products"),
                },
                {
                  path: "products",
                  loader: () => getEntities("products", "?size=500"),
                  element: <Products />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "products/new",
                  loader: async () => {
                    const [manufacturers, priceTypes, productTypes, banners] =
                      await Promise.all([
                        getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                        getEntities("price_types"),
                        getEntities("product_types"),
                        getEntities("banners"),
                      ]);
                    const byName = (a, b) => a.name.localeCompare(b.name);
                    return {
                      entity: {
                        description: "",
                        descriptionFr: "",
                        productType: null,
                        manufacturer: null,
                        priceType: null,
                        status: "",
                        make: "",
                        upcCode: "",
                        formatQut: "",
                        format: "",
                        prices: [],
                      },
                      manufacturers: (manufacturers?.content ?? []).sort(byName),
                      priceTypes: (priceTypes ?? []).sort(byName),
                      productTypes: (productTypes ?? []).sort(byName),
                      banners: (banners?.content ?? []).sort(byName),
                    };
                  },
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      productSchema,
                      [],
                      ["priceType", "productType", "manufacturer"],
                      ["prices"],
                    ),
                  element: <Product />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "products/:id",
                  loader: async ({ params }) => {
                    const [
                      entity,
                      manufacturers,
                      priceTypes,
                      productTypes,
                      banners,
                    ] = await Promise.all([
                      getEntity("products", params.id),
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                      getEntities("price_types"),
                      getEntities("product_types"),
                      getEntities("banners"),
                    ]);
                    const byName = (a, b) => a.name.localeCompare(b.name);
                    return {
                      entity,
                      manufacturers: (manufacturers?.content ?? []).sort(byName),
                      priceTypes: (priceTypes ?? []).sort(byName),
                      productTypes: (productTypes ?? []).sort(byName),
                      banners: (banners?.content ?? []).sort(byName),
                    };
                  },
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      productSchema,
                      [],
                      ["priceType", "productType", "manufacturer"],
                      ["prices"],
                    ),
                  element: <Product />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "users",
                  loader: usersLoader,
                  element: <Users />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "users/new",
                  loader: userLoader,
                  action: userAction,
                  element: <User />,
                },
                {
                  path: "users/:userId",
                  loader: userLoader,
                  action: userAction,
                  element: <User />,
                },
                {
                  path: "banners",
                  loader: () => getEntities("banners", "?size=500"),
                  element: <Banners />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "banners/new",
                  loader: () => ({ entity: { name: "", provinces: [], majorGroup: null } }),
                  action: ({ params, request }) =>
                    entityAction(request, params, bannerSchema, ["provinces"]),
                  element: <Banner />,
                },
                {
                  path: "banners/:id",
                  loader: async ({ params }) => ({
                    entity: await getEntity("banners", params.id),
                  }),
                  action: ({ params, request }) =>
                    entityAction(request, params, bannerSchema, ["provinces"]),
                  element: <Banner />,
                },
                {
                  path: "territories",
                  loader: () => getEntities("territories", "?size=500"),
                  element: <Territories />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "territories/new",
                  loader: () => ({ entity: { name: "", province: "" } }),
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      territorySchema,
                      [],
                      ["province"],
                    ),
                  element: <Territory />,
                },
                {
                  path: "territories/:id",
                  loader: async ({ params }) => ({
                    entity: await getEntity("territories", params.id),
                  }),
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      territorySchema,
                      [],
                      ["province"],
                    ),
                  element: <Territory />,
                },
                {
                  path: "stores",
                  loader: () => getEntities("stores", "?size=500"),
                  element: <Stores />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "stores/new",
                  loader: async () => {
                    const [banners, territories] = await Promise.all([
                      getEntities("banners"),
                      getEntities("territories"),
                    ]);
                    return {
                      entity: {
                        status: null,
                        name: "",
                        classification: null,
                        territory: null,
                        civicNo: "",
                        street: "",
                        office: "",
                        city: "",
                        province: null,
                        banner: null,
                        country: "",
                        postalCode: "",
                        telNo: "",
                        faxNo: "",
                        number: "",
                        webAddress: "",
                        visitFrequency: "",
                        corpo: false,
                      },
                      banners: (banners?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      territories,
                    };
                  },
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      storeSchema,
                      [],
                      ["banner", "territory", "province"],
                      [],
                      ["number", "visitFrequency"],
                    ),
                  element: <Store />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "stores/:id",
                  loader: async ({ params }) => {
                    const [entity, banners, territories] = await Promise.all([
                      getEntity("stores", params.id),
                      getEntities("banners"),
                      getEntities("territories"),
                    ]);
                    return {
                      entity,
                      banners: (banners?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      territories,
                    };
                  },
                  action: ({ params, request }) =>
                    entityAction(
                      request,
                      params,
                      storeSchema,
                      [],
                      ["banner", "territory", "province"],
                      [],
                      ["number", "visitFrequency"],
                    ),
                  element: <Store />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "manufacturers",
                  loader: () => getEntities("manufacturers", "?size=500"),
                  element: <Manufacturers />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "manufacturers/:id",
                  loader: async ({ params }) => ({
                    entity: await getEntity("manufacturers", params.id),
                  }),
                  action: ({ params, request }) =>
                    entityAction(request, params, manufacturerSchema),
                  element: <Manufacturer />,
                },
                {
                  path: "representatives",
                  loader: representativesLoader,
                  element: <Representatives />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "representatives/new",
                  loader: async () => {
                    const territories = await getEntities("territories", "?size=500");
                    return {
                      entity: { lastName: "", firstName: "", territories: [], userName: "", password: "", emailAddress: "" },
                      territories: territories?.content ?? [],
                    };
                  },
                  action: ({ params, request }) =>
                    entityAction(request, params, representativeSchema, ["territories"]),
                  element: <Representative />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "representatives/:id",
                  loader: async ({ params }) => {
                    const [entity, territories] = await Promise.all([
                      getEntity("representatives", params.id),
                      getEntities("territories", "?size=500"),
                    ]);
                    return { entity, territories: territories?.content ?? [] };
                  },
                  action: ({ params, request }) =>
                    entityAction(request, params, representativeSchema, ["territories"]),
                  element: <Representative />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "dashboard-configs",
                  element: <DashboardConfigs />,
                },
                {
                  path: "manufacturer-presets",
                  loader: () => getManufacturerPresets(),
                  element: <ManufacturerPresets />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "manufacturer-presets/new",
                  loader: async () => {
                    const normalize = (r) =>
                      Array.isArray(r) ? r : r?._embedded ? Object.values(r._embedded).flat() : r?.content ?? [];
                    const [mfrs, bns] = await Promise.all([
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                      getEntities("banners", "?size=1000"),
                    ]);
                    const byName = (a, b) => a.name.localeCompare(b.name);
                    return { preset: null, manufacturers: normalize(mfrs).sort(byName), banners: normalize(bns).sort(byName) };
                  },
                  element: <ManufacturerPreset />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "manufacturer-presets/:id",
                  loader: async ({ params }) => {
                    const normalize = (r) =>
                      Array.isArray(r) ? r : r?._embedded ? Object.values(r._embedded).flat() : r?.content ?? [];
                    const [preset, mfrs, bns] = await Promise.all([
                      getManufacturerPreset(params.id),
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                      getEntities("banners", "?size=1000"),
                    ]);
                    const byName = (a, b) => a.name.localeCompare(b.name);
                    return { preset, manufacturers: normalize(mfrs).sort(byName), banners: normalize(bns).sort(byName) };
                  },
                  element: <ManufacturerPreset />,
                  errorElement: <ErrorPage />,
                },
              ],
            },
            {
              path: "features",
              element: <TopRoute sideBarItems={featuresItems} />,
              children: [
                {
                  index: true,
                  loader: () => redirect("/markit/features/tasks"),
                },
                {
                  path: "tasks",
                  loader: () => getEntities("tasks"),
                  element: <Tasks />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "tasks/new",
                  loader: async () => {
                    const [
                      manufacturers,
                      territories,
                      banners,
                      representatives,
                    ] = await Promise.all([
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                      getEntities("territories", "?size=1000"),
                      getEntities("banners", "?size=1000"),
                      getEntities("representatives", "?size=1000"),
                    ]);
                    return {
                      task: {
                        name: "",
                        startDate: "",
                        endDate: "",
                        status: null,
                        stores: [],
                        representatives: [],
                        products: [],
                        questions: [],
                        territories: [],
                        provinces: [],
                        banners: [],
                      },
                      manufacturers: (manufacturers?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      territories: territories?.content ?? [],
                      banners: (banners?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      representatives: (representatives?.content ?? []).sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? "") || (a.firstName ?? "").localeCompare(b.firstName ?? "")),
                    };
                  },
                  element: <Task />,
                },
                {
                  path: "tasks/:id",
                  loader: async ({ params }) => {
                    const [
                      manufacturers,
                      territories,
                      banners,
                      representatives,
                      task,
                    ] = await Promise.all([
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                      getEntities("territories", "?size=1000"),
                      getEntities("banners", "?size=1000"),
                      getEntities("representatives", "?size=1000"),
                      getEntity("tasks", params.id),
                    ]);
                    task.stores = task.stores.map((s) => ({
                      id: s.taskStoreKey.storeId,
                    }));
                    const repList = representatives?.content ?? [];
                    task.representatives = task.representatives.map((r) =>
                      repList.find(
                        (rep) =>
                          rep.id === r.taskRepresentativeKey.representativeId,
                      ),
                    );
                    if (task.startDate) task.startDate = new Date(task.startDate).toISOString().slice(0, 10);
                    if (task.endDate) task.endDate = new Date(task.endDate).toISOString().slice(0, 10);
                    return {
                      task,
                      manufacturers: (manufacturers?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      territories: territories?.content ?? [],
                      banners: (banners?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                      representatives: repList.sort((a, b) => (a.lastName ?? "").localeCompare(b.lastName ?? "") || (a.firstName ?? "").localeCompare(b.firstName ?? "")),
                    };
                  },
                  element: <Task />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "bookings",
                  loader: bookingsLoader,
                  element: <Bookings />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "bookings/new",
                  loader: bookingLoader,
                  element: <Booking />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "bookings/:id",
                  loader: bookingLoader,
                  element: <Booking />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "hot-sheets",
                  loader: hotSheetsLoader,
                  element: <HotSheets />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "hot-sheets/new",
                  loader: hotSheetLoader,
                  element: <HotSheet />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "hot-sheets/:id",
                  loader: hotSheetLoader,
                  element: <HotSheet />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "promotions",
                  loader: () => getEntities("promotions"),
                  element: <Promotions />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "promotions/new",
                  loader: () => ({ entity: { name: "" } }),
                  action: ({ params, request }) =>
                    entityAction(request, params, promotionSchema),
                  element: <Promotion />,
                },
                {
                  path: "promotions/:id",
                  loader: async ({ params }) => ({
                    entity: await getEntity("promotions", params.id),
                  }),
                  action: ({ params, request }) =>
                    entityAction(request, params, promotionSchema),
                  element: <Promotion />,
                },
                {
                  path: "photo-hashtags",
                  loader: () => getEntities("photoHashtags"),
                  element: <PhotoHashtags />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "photo-hashtags/new",
                  loader: async () => ({
                    entity: { hashtag: "", manufacturer: null },
                    manufacturers: ((await getEntities("manufacturers", "?search=status:ACTIVE&size=1000"))?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)),
                  }),
                  element: <PhotoHashtag />,
                },
                {
                  path: "photo-hashtags/:id",
                  loader: async ({ params }) => {
                    const [entity, manufacturersResp] = await Promise.all([
                      getEntity("photoHashtags", params.id),
                      getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                    ]);
                    return { entity, manufacturers: (manufacturersResp?.content ?? []).sort((a, b) => a.name.localeCompare(b.name)) };
                  },
                  element: <PhotoHashtag />,
                  errorElement: <ErrorPage />,
                },
              ],
            },
            {
              path: "dashboard",
              element: <Dashboard />,
              errorElement: <ErrorPage />,
            },
            {
              path: "performance",
              element: <PerformanceLayout sideBarItems={performanceItems} />,
              children: [
                {
                  index: true,
                  loader: () => redirect("/markit/performance/call_completion"),
                },
                {
                  path: "call_completion",
                  element: <CallCompletionReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "distribution_drop",
                  element: <DistributionDropReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "photo_capture",
                  element: <PhotoCaptureReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "call_time",
                  element: <CallTimeReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "rep_report",
                  element: <RepReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "visit_report",
                  element: <VisitReport />,
                  errorElement: <ErrorPage />,
                },
              ],
            },
            {
              path: "reports",
              element: <ReportsLayout sideBarItems={reportsItems} />,
              children: [
                {
                  index: true,
                  loader: async () => {
                    try {
                      const response = await fetch(`${AUTH_API}/me`, { credentials: "include" });
                      const auth = response.ok ? await response.json() : null;
                      if (auth?.type === "MANUFACTURER") {
                        return redirect("/markit/reports/distribution_by_banner_by_item");
                      }
                    } catch {
                      // fall through to the default redirect below
                    }
                    return redirect("/markit/reports/distribution_audit");
                  },
                },
                {
                  path: "list_products",
                  loader: () => getEntities("manufacturers", "?search=status:ACTIVE&size=1000"),
                  element: <Report />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "distribution_audit",
                  loader: () => null,
                  element: <DistributionAuditReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "distribution_detail",
                  loader: () => null,
                  element: <DistributionDetailReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "distribution_by_banner_by_item",
                  loader: () => null,
                  element: <DistributionByBannerByItemReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "total_distribution_by_banner",
                  loader: () => null,
                  element: <TotalDistributionByBannerReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "stock_level_summary",
                  loader: () => null,
                  element: <DistributionSummaryReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "monthly_merchandising",
                  element: <MonthlyMerchandisingReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "banner_by_month",
                  element: <BannerByMonthReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "pictures",
                  element: <PicturesReport />,
                  errorElement: <ErrorPage />,
                },
                {
                  path: "task_report",
                  element: <TaskReport />,
                  errorElement: <ErrorPage />,
                },
              ],
            },
          ],
        },
      ],
    },
  ]);

  return <RouterProvider router={router} />;
}

export function Fallback() {
  return <p>Performing initial data load</p>;
}

function RedirectToLogin() {
  const navigate = useNavigate();

  useEffect(() => {
    navigate("/login");
  }, []);
}

export default App;
